import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gift } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { inr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function GiftCards() {
  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [recipient, setRecipient] = useState({ name: "", email: "", message: "" });
  const [checkCode, setCheckCode] = useState("");
  const [checkResult, setCheckResult] = useState(null);
  const [redeemCode, setRedeemCode] = useState("");
  const { addItem } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/products/gift-card").then((r) => {
      setProduct(r.data);
      setVariant((r.data.variants || [])[1] || (r.data.variants || [])[0]);
    }).catch(() => {});
  }, []);

  const buy = () => {
    if (!product || !variant) return;
    if (recipient.email && !/^\S+@\S+\.\S+$/.test(recipient.email)) return toast.error("Recipient email looks wrong");
    const v = { ...variant, pricing: { subtotal: variant.price_override, display_price: variant.price_override, overridden: true, final: variant.price_override } };
    const pers = recipient.name || recipient.email || recipient.message
      ? { selections: {}, summary: `For ${recipient.name || recipient.email}${recipient.message ? ` — "${recipient.message}"` : ""}`, surcharge: 0, gift_card: { recipient_name: recipient.name, recipient_email: recipient.email, message: recipient.message } }
      : null;
    addItem({ ...product, rate_info: { gst_rate: 0 } }, v, 1, pers);
  };

  const check = async () => {
    try {
      const r = await api.post("/gift-cards/check", { code: checkCode.trim() });
      setCheckResult(r.data);
    } catch (e) { setCheckResult(null); toast.error(errMsg(e)); }
  };

  const redeem = async () => {
    if (!user) { navigate("/login?next=/gift-cards"); return; }
    try {
      const r = await api.post("/gift-cards/redeem", { code: redeemCode.trim() });
      toast.success(`${inr(r.data.credited)} added to your wallet`);
      setRedeemCode("");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="container-x py-12 max-w-4xl" data-testid="gift-cards-page">
      <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold">Gift Cards</p>
      <h1 className="font-heading text-4xl sm:text-5xl font-semibold mt-2">The gift that always fits</h1>
      <p className="text-stone-600 mt-3 max-w-2xl">
        A Siyaara code, delivered by email, redeemable into a wallet balance against any piece on the store.
      </p>

      <section className="mt-10 border border-stone-200 bg-white p-6 sm:p-8" data-testid="gift-card-buy">
        <h2 className="font-heading font-semibold text-lg flex items-center gap-2"><Gift size={18} className="text-brand" /> Choose an amount</h2>
        <div className="flex flex-wrap gap-2 mt-4">
          {(product?.variants || []).map((v) => (
            <button key={v.id} onClick={() => setVariant(v)}
              className={`px-5 py-3 border font-heading font-semibold transition-colors ${variant?.id === v.id ? "border-brand bg-brand text-ivory" : "border-stone-200 bg-white hover:border-brand"}`}
              data-testid={`gc-denom-${v.price_override}`}>
              {inr(v.price_override)}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
          <div><Label>Recipient name</Label><Input value={recipient.name} onChange={(e) => setRecipient({ ...recipient, name: e.target.value })} data-testid="gc-recipient-name" /></div>
          <div><Label>Recipient email</Label><Input type="email" value={recipient.email} onChange={(e) => setRecipient({ ...recipient, email: e.target.value })} data-testid="gc-recipient-email" /></div>
          <div><Label>Message</Label><Input value={recipient.message} onChange={(e) => setRecipient({ ...recipient, message: e.target.value })} data-testid="gc-recipient-message" /></div>
        </div>
        <Button onClick={buy} disabled={!variant} className="mt-5 h-11 px-8 bg-stone-900 hover:bg-brand text-white btn-press" data-testid="gc-add-to-cart">
          Add {variant ? inr(variant.price_override) : ""} gift card to cart
        </Button>
        <p className="text-[11px] text-stone-500 mt-2">The code is issued after payment and can be emailed to the recipient or shared by you.</p>
      </section>

      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        <section className="border border-stone-200 bg-white p-6" data-testid="gift-card-check">
          <h3 className="font-heading font-semibold">Check a balance</h3>
          <div className="flex gap-2 mt-3">
            <Input value={checkCode} onChange={(e) => setCheckCode(e.target.value.toUpperCase())} placeholder="SYR-XXXX-XXXX-XX" className="font-mono" data-testid="gc-check-input" />
            <Button variant="outline" onClick={check} data-testid="gc-check-btn">Check</Button>
          </div>
          {checkResult && (
            <p className="text-sm mt-3" data-testid="gc-check-result">
              Balance: <strong>{inr(checkResult.balance)}</strong> of {inr(checkResult.amount)} — <span className={checkResult.status === "active" ? "text-emerald-700" : "text-stone-500"}>{checkResult.status}</span>
            </p>
          )}
        </section>
        <section className="border border-stone-200 bg-white p-6" data-testid="gift-card-redeem">
          <h3 className="font-heading font-semibold">Redeem to your wallet</h3>
          <div className="flex gap-2 mt-3">
            <Input value={redeemCode} onChange={(e) => setRedeemCode(e.target.value.toUpperCase())} placeholder="SYR-XXXX-XXXX-XX" className="font-mono" data-testid="gc-redeem-input" />
            <Button variant="outline" onClick={redeem} data-testid="gc-redeem-btn">Redeem</Button>
          </div>
          <p className="text-[11px] text-stone-500 mt-2">{user ? `Credits the wallet of ${user.email}` : "You'll sign in first — the credit lands in your wallet."}</p>
        </section>
      </div>
    </div>
  );
}
